import { Ray, Vector2, Vector3, Vector4 } from 'three';
import { SettingState } from '../../../cross-team-shared/state/setting-state';
import { Point3D } from '../../../team-apis/otterdam/answers/shape-typings';
import { PointCloudContentState } from '../../state/point-cloud-content-state';
import { getCameraData } from '../calibration-utils';
import {
  AbstractProjectionUtils,
  ProjectTo2DOptions,
} from './abstract-projection-utils';

export class CdiCylindricalProjectionUtils extends AbstractProjectionUtils {
  protected readonly principlePoint = new Vector2();
  protected readonly focalLength = new Vector2();
  protected readonly position = new Vector4();

  projectTo2D(
    point: Point3D,
    settingState: SettingState,
    state: PointCloudContentState,
    options: ProjectTo2DOptions,
  ) {
    this.updateProjectionCamera(state, settingState, options.outputName);
    this.setUpIntrinsics(settingState, state, options);

    const pixel = this.lightrayToPixel(point, state, options);

    return {
      position: pixel.toArray(),
      isInsideFrustum: !this.is2DPointBehindCamera(point, options),
    };
  }

  projectTo3D(): Ray {
    throw new Error('3D projection not supported for CDI cylindrical model');
  }

  protected setUpIntrinsics(
    settingState: SettingState,
    state: PointCloudContentState,
    options: ProjectTo2DOptions,
  ) {
    const { principlePoint, focalLength } = getCameraData(
      state.calibrationData[options.outputName],
      settingState.calibrationOptions[options.outputName],
      state.render.frameNumber,
    );

    this.principlePoint.fromArray(principlePoint);
    this.focalLength.fromArray(focalLength);
  }

  protected lightrayToPixel(
    point: Point3D,
    state: PointCloudContentState,
    options: ProjectTo2DOptions,
  ) {
    const imageSize = this.getImageSize(state, options.outputName);
    this.position.fromArray([...point, 1]);

    const vertexInCamera = this.position
      .applyMatrix4(options.modelMatrix)
      .applyMatrix4(this.projectionCamera.matrixWorldInverse);

    const { x, y, z } = vertexInCamera;

    // Original formula provided by DBE and CDI
    const u = this.principlePoint.x - this.focalLength.x * Math.atan2(x, -z);
    const v =
      this.principlePoint.y -
      (this.focalLength.y * y) / new Vector2(z, x).length();

    return new Vector2().subVectors(imageSize, new Vector2(u, v));
  }

  protected is2DPointBehindCamera(point: Point3D, options: ProjectTo2DOptions) {
    const cameraWorldPosition = this.projectionCamera.getWorldPosition(
      new Vector3(),
    );
    const cameraWorldDirection = this.projectionCamera.getWorldDirection(
      new Vector3(),
    );

    this.position.fromArray([...point, 1]);

    const vertexWorldPosition = new Vector3().fromArray(
      this.position.applyMatrix4(options.modelMatrix).toArray(),
    );

    const cameraToPointDirection = new Vector3();

    cameraToPointDirection
      .subVectors(vertexWorldPosition, cameraWorldPosition)
      .normalize();

    const behindCameraTolerance = -0.1;
    return (
      cameraWorldDirection.dot(cameraToPointDirection) < behindCameraTolerance
    );
  }
}
